// Workflow Copilot popup — a Claude Code-style chat sheet that slides up from
// the bottom of the canvas. The agent (Fast = Haiku by default, Balanced =
// Sonnet via the header dropdown) works the workflow through
// TOOLS: grepWorkflow/getNode/listNodes to find things, and write tools
// (addNode, deleteNode, addConnection, ...) that mutate workflowState LIVE
// through onApplyEdits — so the canvas updates step by step while the agent
// works, with undo history and dirty-marking handled by the existing effects.

var React = require('react');
var { createElement: h, useState, useRef, useEffect } = React;
var workflowCopilot = require('../lib/workflowCopilot');
var { captureWorkflowCanvas } = require('../lib/captureCanvas');
var { renderChatMarkdown } = require('../lib/renderChatMarkdown');
var { createSingleNodeDiscovery } = require('../lib/singleNodeDiscovery');
var { formatText, stripStreamingXml, parseAiMessage, optionLabel, selectionList, selectionText } = require('../lib/plannerChatFormat');
require('./WorkflowCopilotPopup.css');

// Cut the streamed text at the <tool> block so raw tool syntax never flashes
// in the live view while the model is still writing it.
function stripToolForDisplay(text) {
  var idx = String(text || '').indexOf('<tool');
  return idx === -1 ? String(text || '') : String(text || '').slice(0, idx);
}

// Canvas-mutating tools — a finished turn that ran any of these successfully
// offers a "Run QA test" follow-up button under the reply.
var WRITE_TOOL_NAMES = {
  setWorkflowName: true, addNode: true, updateNode: true, editNodeCode: true,
  deleteNode: true, addConnection: true, updateConnection: true, deleteConnection: true
};

// One trace chip per tool call: what ran and how it went.
//   kind: 'ok' (write applied) | 'err' (tool errored) | 'info' (read tool)
function traceEntryFor(t) {
  var result = t.result || {};
  var args = t.args || {};
  if (result.error) {
    return { kind: 'err', label: t.name + ' — ' + result.error };
  }
  if (t.name === 'grepWorkflow') {
    var found = (result.nodes || []).length;
    return { kind: 'info', label: 'Searched /' + (args.pattern || '') + '/ — ' + found + ' node' + (found === 1 ? '' : 's') };
  }
  if (t.name === 'getNode') return { kind: 'info', label: 'Read node ' + (args.nodeId || '') };
  if (t.name === 'getNodeOutput') return { kind: 'info', label: 'Inspected output of ' + (args.nodeId || '') };
  if (t.name === 'screenshotWorkflow') return { kind: 'info', label: 'Captured a screenshot of the workflow' };
  if (t.name === 'aiReviewWorkflow' || t.name === 'aiReviewNode') {
    var scope = t.name === 'aiReviewNode' ? 'node' : 'workflow';
    var count = (result.anomalies || []).length;
    return count === 0
      ? { kind: 'ok', label: 'Reviewed the ' + scope + ' — no anomalies' }
      : { kind: 'info', label: 'Reviewed the ' + scope + ' — ' + count + ' anomaly' + (count === 1 ? '' : 'ies') + ' found' };
  }
  if (t.name === 'listNodes') return { kind: 'info', label: 'Listed nodes & connections' };
  if (result.ok === false && result.note) return { kind: 'info', label: result.note };
  return { kind: 'ok', label: result.did || result.note || t.name };
}

function WorkflowCopilotPopup(props) {
  var onClose = props.onClose;
  var onApplyEdits = props.onApplyEdits;   // (ops) => { applied, errors }
  var onLocateNode = props.onLocateNode || null;
  // Clicks the real play/run button in the canvas footer — the "Run QA test"
  // follow-up uses it to actually execute the workflow (not a chat turn).
  var onRunWorkflow = props.onRunWorkflow || null;

  var [messages, setMessages] = useState([]);
  var [input, setInput] = useState('');
  var [sending, setSending] = useState(false);
  // Driver tier: 'fast' (Haiku, default) | 'balanced' (Sonnet) — header dropdown.
  var [tier, setTier] = useState('fast');
  var [liveText, setLiveText] = useState('');
  var [liveTrace, setLiveTrace] = useState([]);
  // When the copilot's addNode(custom/agent) tool runs, the single-node planner
  // discovery (probe + Q&A) runs INSIDE this chat: a headless driver
  // (lib/singleNodeDiscovery) streams the planner and this sheet renders its
  // probes/questions natively — answers go through the copilot's own input bar.
  // The tool's promise stays pending until the driver produces a <node> spec or
  // the discovery is cancelled. Only one runs at a time — the copilot tool loop
  // is serial (one pending_client_tool).
  //   discovery: driver state + { sourceTitle } (see createSingleNodeDiscovery)
  //   discoverySelections: per-question chip selections for the awaiting turn
  var [discovery, setDiscovery] = useState(null);
  var [discoverySelections, setDiscoverySelections] = useState({});
  var discoveryHandleRef = useRef(null);
  var onBuildSingleNodeContextRef = useRef(props.onBuildSingleNodeContext);
  onBuildSingleNodeContextRef.current = props.onBuildSingleNodeContext;
  // True while the discovery waits for the user — the main input bar answers it.
  var discoveryAwaiting = !!(discovery && discovery.phase === 'awaiting');

  // Always hand the tools the CURRENT workflow (props update every render;
  // the refs keep async closures from capturing stale values).
  var workflowStateRef = useRef(props.workflowState);
  workflowStateRef.current = props.workflowState;
  var onApplyEditsRef = useRef(onApplyEdits);
  onApplyEditsRef.current = onApplyEdits;
  var onGenerateCustomNodeRef = useRef(props.onGenerateCustomNode);
  onGenerateCustomNodeRef.current = props.onGenerateCustomNode;
  var onEditNodeWithAskCodeRef = useRef(props.onEditNodeWithAskCode);
  onEditNodeWithAskCodeRef.current = props.onEditNodeWithAskCode;
  var onGetAskCodeEditorStateRef = useRef(props.onGetAskCodeEditorState);
  onGetAskCodeEditorStateRef.current = props.onGetAskCodeEditorState;
  var onGetVariableValuesRef = useRef(props.onGetVariableValues);
  onGetVariableValuesRef.current = props.onGetVariableValues;

  var traceRef = useRef([]);
  // True once the current turn applies a write tool — drives the QA offer.
  var editedRef = useRef(false);
  var bodyRef = useRef(null);
  var inputRef = useRef(null);
  // Abort handle for the in-flight agentic loop (workflowCopilot attaches
  // .abort() to it) — "Stop & close" uses it to halt the model loop for real.
  var controlRef = useRef(null);
  // Close-while-busy needs a confirm step: closing tears down the loop and the
  // discovery, so we never do it silently mid-turn.
  var [confirmClose, setConfirmClose] = useState(false);
  var busyRef = useRef(false);
  busyRef.current = sending || !!discovery;

  function stopAndClose() {
    if (controlRef.current && typeof controlRef.current.abort === 'function') {
      try { controlRef.current.abort(); } catch (e) {}
    }
    var d = discoveryHandleRef.current;
    if (d) d.stop('Copilot closed before the node was built.');
    if (typeof onClose === 'function') onClose();
  }

  function requestClose() {
    if (busyRef.current) { setConfirmClose(true); return; }
    if (typeof onClose === 'function') onClose();
  }

  // Autoscroll to the newest message / live delta / trace chip / discovery turn.
  useEffect(function() {
    var el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, liveText, liveTrace, discovery, discoverySelections]);

  // Escape closes (matches RunInputModal behavior) — via requestClose so a
  // busy copilot asks for confirmation instead of dying silently.
  useEffect(function() {
    function onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (busyRef.current) setConfirmClose(true);
      else if (typeof onClose === 'function') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return function() { document.removeEventListener('keydown', onKeyDown); };
  }, [onClose]);

  // The turn finished while the confirm bar was up — nothing to stop anymore.
  useEffect(function() {
    if (!sending && !discovery) setConfirmClose(false);
  }, [sending, discovery]);

  useEffect(function() {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // If the sheet unmounts mid-discovery (user closed the copilot), stop the
  // driver so the paused tool loop settles instead of dangling.
  useEffect(function() {
    return function() {
      var d = discoveryHandleRef.current;
      if (d) d.stop('Copilot closed before the node was built.');
    };
  }, []);

  // First message typed into the launcher pill — send it as soon as the
  // sidebar mounts so "type in the pill → Enter" flows straight into a chat.
  // Guarded to fire ONCE per component lifetime: effect re-runs on the same
  // instance (dev Fast Refresh re-executes []-dep effects on every hot edit,
  // state intact) were re-sending the pill message into the live chat.
  var initialSentRef = useRef(false);
  useEffect(function() {
    if (initialSentRef.current) return;
    initialSentRef.current = true;
    var first = typeof props.initialText === 'string' ? props.initialText.trim() : '';
    if (first) send(first);
  }, []);

  // Two-phase custom/agent node creation for the copilot's addNode tool.
  //
  // Phase A — DISCOVERY: run the single-node planner (probe + Q&A) headlessly
  // and render it in THIS chat — probe chips, clarifying questions, streaming
  // text. The copilot tool loop stays paused (pending_client_tool) while this
  // runs; the user answers questions right in the copilot input bar.
  //
  // Phase B — CODEGEN: hand the finished spec to props.onGenerateCustomNode
  // (generateNodeAfterSource) which runs the real codegen, drops the node wired
  // after its source, and resolves { nodeId, title } back to the tool loop.
  function runAddNodeDiscovery(args) {
    var a = args || {};
    var sourceNodeId = String(a.sourceNodeId || '').trim();
    var prompt = String(a.prompt || '').trim();
    if (!sourceNodeId) return Promise.reject(new Error('addNode: sourceNodeId is required'));
    if (!prompt) return Promise.reject(new Error('addNode: prompt is required'));

    var buildCtx = onBuildSingleNodeContextRef.current;
    var singleNodeContext = typeof buildCtx === 'function' ? buildCtx(sourceNodeId) : null;
    var branch = a.branch === 'yes' || a.branch === 'no' ? a.branch : null;
    var fromAnchor = typeof a.fromAnchor === 'string' && a.fromAnchor ? a.fromAnchor : null;
    var sourceTitle = (singleNodeContext && singleNodeContext.sourceNodeTitle) || '';

    var handle = createSingleNodeDiscovery({
      initialPrompt: prompt,
      agentMode: !!a.agentMode,
      responseFields: Array.isArray(a.responseFields) ? a.responseFields : null,
      singleNodeContext: singleNodeContext,
      onState: function(s) {
        setDiscovery(Object.assign({ sourceTitle: sourceTitle }, s));
        if (s.phase === 'awaiting') setDiscoverySelections({});
      }
    });
    discoveryHandleRef.current = handle;

    function teardown() {
      discoveryHandleRef.current = null;
      setDiscovery(null);
      setDiscoverySelections({});
    }

    return handle.result.then(function(spec) {
      teardown();
      var doGen = onGenerateCustomNodeRef.current;
      if (typeof doGen !== 'function') {
        throw new Error('AI node generation is not available right now.');
      }
      return doGen({
        sourceNodeId: sourceNodeId,
        prompt: String(spec.prompt || prompt),
        agentMode: !!spec.agentMode,
        responseFields: Array.isArray(spec.responseFields) ? spec.responseFields : null,
        branch: branch,
        fromAnchor: fromAnchor
      });
    }).catch(function(err) {
      teardown();
      throw err;
    });
  }

  // Send an answer to the awaiting discovery turn. The question turn and the
  // user's answer stay in the copilot transcript (the question renders its
  // chips statically once answered).
  function sendDiscoveryAnswer(text) {
    var t = String(text || '').trim();
    var d = discoveryHandleRef.current;
    if (!t || !d || !discoveryAwaiting) return;
    var q = discovery && discovery.question;
    setMessages(function(prev) {
      var next = prev;
      if (q && q.raw) next = next.concat([{ role: 'assistant', kind: 'discovery', content: q.raw, displayContent: q.display }]);
      return next.concat([{ role: 'user', content: t }]);
    });
    setInput('');
    setDiscoverySelections({});
    d.answer(t);
  }

  // Chip selections → one combined answer ("Question: choice" per line).
  function submitDiscoverySelections() {
    var q = discovery && discovery.question;
    if (!q) return;
    var questions = (q.parts || []).filter(function(p) { return p.type === 'question' || p.type === 'input'; });
    var parts = [];
    questions.forEach(function(qq, i) {
      // Multi-select answers arrive as an array — selectionText joins them.
      var ans = selectionText(discoverySelections[i]);
      if (ans) {
        parts.push(qq.bareReply ? ans : qq.question + ': ' + ans);
      }
    });
    if (parts.length > 0) sendDiscoveryAnswer(parts.join('\n'));
  }

  // Optional override lets the launcher pill hand in a first message that
  // sends itself on mount (see the effect below); normal sends read the input.
  function send(textOverride) {
    var text = (typeof textOverride === 'string' ? textOverride : input).trim();
    if (!text || sending) return;

    var nextMessages = messages.concat([{ role: 'user', content: text }]);
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    setLiveText('');
    traceRef.current = [];
    editedRef.current = false;
    setLiveTrace([]);
    controlRef.current = {};

    workflowCopilot.sendMessageStream({
      control: controlRef.current,
      tier: tier,
      getWorkflowState: function() { return workflowStateRef.current; },
      applyEdits: function(ops) {
        if (typeof onApplyEditsRef.current !== 'function') {
          return { applied: [], errors: ['Editing is not available right now.'] };
        }
        var out = onApplyEditsRef.current(ops) || {};
        // The canvas hands back the post-edit state — adopt it synchronously
        // so the resume snapshot (taken before React re-renders) already
        // contains the edit the model just made.
        if (out.state) workflowStateRef.current = out.state;
        return { applied: out.applied || [], errors: out.errors || [] };
      },
      // Custom/agent nodes go through probe + Q&A discovery IN this sheet
      // (runAddNodeDiscovery) before codegen — not straight to generation.
      generateCustomNode: function(args) {
        return runAddNodeDiscovery(args);
      },
      editNodeWithAskCode: typeof props.onEditNodeWithAskCode === 'function'
        ? function(nodeId, prompt) { return onEditNodeWithAskCodeRef.current(nodeId, prompt); }
        : null,
      getAskCodeEditorState: typeof props.onGetAskCodeEditorState === 'function'
        ? function() { return onGetAskCodeEditorStateRef.current(); }
        : null,
      // Live variable values from the canvas (ephemeral run/preview map) —
      // read tools surface each variable's CURRENT value from these.
      getVariableValues: function() {
        return typeof onGetVariableValuesRef.current === 'function'
          ? onGetVariableValuesRef.current()
          : {};
      },
      captureCanvas: function() {
        var canvasEl = props.canvasRef && props.canvasRef.current;
        return captureWorkflowCanvas(canvasEl);
      },
      messages: nextMessages,
      onChunk: function(chunk) { setLiveText(stripToolForDisplay(chunk.fullText)); },
      onRound: function() { setLiveText(''); },
      onToolTrace: function(t) {
        if (WRITE_TOOL_NAMES[t.name] && !(t.result && t.result.error)) editedRef.current = true;
        traceRef.current = traceRef.current.concat([traceEntryFor(t)]);
        setLiveTrace(traceRef.current);
      }
    }).then(function(result) {
      setMessages(function(prev) {
        return prev.concat([{
          role: 'assistant',
          content: result.reply || (traceRef.current.length > 0 ? 'Done.' : ''),
          trace: traceRef.current,
          offerQa: editedRef.current
        }]);
      });
    }).catch(function(err) {
      if (err && err.aborted) return; // user hit "Stop & close" — no error bubble
      setMessages(function(prev) {
        return prev.concat([{
          role: 'assistant',
          content: '',
          trace: traceRef.current.concat([{
            kind: 'err',
            label: (err && err.message) || 'Something went wrong. Please try again.'
          }]),
          // Edits may have landed before the failure — QA is useful then too.
          offerQa: editedRef.current
        }]);
      });
    }).then(function() {
      setSending(false);
      setLiveText('');
      setLiveTrace([]);
    });
  }

  function onInputKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (discoveryAwaiting) sendDiscoveryAnswer(input);
      else send();
    }
  }

  function stop(e) { e.stopPropagation(); }

  function renderTraceChips(trace, keyPrefix) {
    if (!trace || trace.length === 0) return null;
    return h('div', { className: 'copilot-trace-list' },
      trace.map(function(entry, i) {
        return h('div', {
          key: keyPrefix + '-' + i,
          className: 'copilot-trace-chip copilot-trace-chip--' + entry.kind
        }, (entry.kind === 'ok' ? '✓ ' : entry.kind === 'err' ? '⚠ ' : '· ') + entry.label);
      })
    );
  }

  // The awaiting discovery turn: question text + clickable option chips (same
  // planner-msg-* look as the balloon/planner). Free-text answers go through
  // the main input bar below, so there is no separate "Other…" field here.
  function renderDiscoveryQuestion(q) {
    var qIdx = 0;
    var els = [];
    (q.parts || []).forEach(function(part, pIdx) {
      if (part.type === 'text') {
        if (part.text && part.text.trim()) {
          els.push(h('div', { key: 't-' + pIdx, className: 'copilot-md' }, formatText(part.text)));
        }
        return;
      }
      if (part.type !== 'question' && part.type !== 'input') return;
      var myIdx = qIdx++;
      // multi="true" → stay on the chips and toggle several; a single-answer
      // question collapses to "<answer> · Change" as soon as one is picked.
      var multi = !!part.multi;
      var chosen = selectionList(discoverySelections[myIdx]);
      function setAnswer(value) {
        var c = Object.assign({}, discoverySelections);
        if (selectionText(value)) c[myIdx] = value; else delete c[myIdx];
        setDiscoverySelections(c);
      }
      var chips = h('div', { className: 'planner-msg-options' },
        (part.options || []).map(function(opt, oi) {
          var label = optionLabel(opt);
          var isSel = chosen.indexOf(label) !== -1;
          return h('button', {
            key: oi,
            className: 'planner-msg-option-btn' + (isSel ? ' planner-msg-option-selected' : ''),
            onClick: function() {
              if (!multi) { setAnswer(label); return; }
              setAnswer(isSel
                ? chosen.filter(function(l) { return l !== label; })
                : chosen.concat([label]));
            }
          }, label);
        })
      );
      els.push(h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
        h('div', { className: 'planner-msg-question-label' }, part.question),
        multi ? h('div', { className: 'planner-msg-question-hint' }, 'Select all that apply') : null,
        part.type === 'question'
          ? (!multi && chosen.length > 0
              ? h('div', { className: 'planner-msg-selected-answer' },
                  h('span', { className: 'planner-msg-selected-value' }, chosen[0]),
                  h('button', {
                    className: 'planner-msg-change-btn',
                    onClick: function() { setAnswer(''); }
                  }, 'Change')
                )
              : chips)
          : h('div', { className: 'copilot-live-placeholder' }, 'Type your answer in the box below.')
      ));
    });
    var answered = Object.keys(discoverySelections).filter(function(k) {
      return discoverySelections[k] && String(discoverySelections[k]).trim();
    }).length;
    if (answered > 0) {
      els.push(h('button', {
        key: 'send',
        className: 'ai-flow-chat-submit-btn',
        onClick: submitDiscoverySelections
      }, 'Send answer' + (answered > 1 ? 's' : '')));
    }
    return els;
  }

  function renderMessage(msg, idx) {
    var isUser = msg.role === 'user';
    // Answered discovery turns carry planner XML — render text + static chips
    // (renderChatMarkdown would show the raw <question> tags).
    if (!isUser && msg.kind === 'discovery') {
      var parsed = parseAiMessage(msg.displayContent || msg.content);
      return h('div', { key: 'msg-' + idx, className: 'copilot-msg copilot-msg--ai' },
        parsed.parts.map(function(part, pIdx) {
          if (part.type === 'text') {
            return part.text && part.text.trim()
              ? h('div', { key: pIdx, className: 'copilot-md' }, formatText(part.text))
              : null;
          }
          if (part.type === 'question' || part.type === 'input') {
            return h('div', { key: pIdx, className: 'planner-msg-question-block' },
              h('div', { className: 'planner-msg-question-label' }, part.question),
              h('div', { className: 'planner-msg-options-static' },
                (part.options || []).map(function(opt, oi) {
                  return h('div', { key: oi, className: 'planner-msg-option-static' }, '• ' + optionLabel(opt));
                })
              )
            );
          }
          return null;
        })
      );
    }
    return h('div', {
      key: 'msg-' + idx,
      className: 'copilot-msg ' + (isUser ? 'copilot-msg--user' : 'copilot-msg--ai')
    },
      !isUser ? renderTraceChips(msg.trace, 'msg-' + idx) : null,
      msg.content
        ? h('div', { className: 'copilot-md' },
            isUser ? msg.content : renderChatMarkdown(msg.content, 'copilot', { onNodeClick: onLocateNode })
          )
        : null
    );
  }

  return h('div', {
    className: 'copilot-popup' + (props.docked ? ' copilot-popup--docked' : ''),
    onMouseDown: stop, onTouchStart: stop, onClick: stop, onWheel: stop
  },
    // Header
    h('div', { className: 'copilot-header' },
      h('div', { className: 'copilot-header-left' },
        h('span', { className: 'copilot-dot', 'aria-hidden': 'true' }),
        h('span', { className: 'copilot-title' }, 'Workflow Copilot'),
        h('span', { className: 'copilot-badge' }, 'edits your canvas')
      ),
      h('div', { className: 'copilot-header-right' },
        // Driver tier — applies from the NEXT message (a running turn keeps
        // the model it started with).
        h('select', {
          className: 'copilot-tier-select',
          title: 'Model: Fast (Haiku) or Balanced (Sonnet)',
          value: tier,
          onChange: function(e) { setTier(e.target.value); }
        },
          h('option', { value: 'fast' }, 'Fast'),
          h('option', { value: 'balanced' }, 'Balanced')
        ),
        h('button', { type: 'button', className: 'copilot-close', title: 'Close', onClick: requestClose }, '×')
      )
    ),

    // Messages
    h('div', { className: 'copilot-body', ref: bodyRef },
      messages.length === 0 && !sending
        ? h('div', { className: 'copilot-empty' },
            'Ask me to change this workflow — rename nodes, rewire connections, tweak params, add branches…'
          )
        : null,
      messages.map(renderMessage),
      // "Run QA test" follow-up — offered under the newest reply once a turn
      // that changed the canvas finishes. Clicking runs the workflow (clicks
      // the real play button) so the edit is verified by an actual execution.
      !sending && !discovery && messages.length > 0
        && messages[messages.length - 1].role === 'assistant'
        && messages[messages.length - 1].offerQa
        && onRunWorkflow
        ? h('div', { className: 'copilot-qa-row' },
            h('button', {
              type: 'button',
              className: 'copilot-qa-btn',
              onClick: function() { onRunWorkflow(); }
            }, '✓ Run QA test')
          )
        : null,
      // While a node discovery is live it renders its own status below — hide
      // the generic "Working…" line (but keep trace chips from earlier calls).
      sending
        ? h('div', { className: 'copilot-msg copilot-msg--ai' },
            renderTraceChips(liveTrace, 'live'),
            discovery
              ? null
              : h('div', { className: 'copilot-live' },
                  h('span', { className: 'copilot-spinner', 'aria-hidden': 'true' }),
                  liveText
                    ? h('div', { className: 'copilot-md copilot-live-text' }, renderChatMarkdown(liveText, 'copilot'))
                    : h('span', { className: 'copilot-live-placeholder' },
                        liveTrace.length > 0 ? 'Working…' : 'Reading the workflow…')
                )
          )
        : null,
      // Live single-node discovery for addNode(custom/agent): the planner's
      // probing + questions render natively in this chat; the user answers via
      // the option chips or the main input bar.
      discovery
        ? h('div', { className: 'copilot-msg copilot-msg--ai copilot-discovery' },
            h('div', { className: 'copilot-addnode-label' },
              discoveryAwaiting ? null : h('span', { className: 'copilot-spinner', 'aria-hidden': 'true' }),
              'Building node' + (discovery.sourceTitle ? ' after "' + discovery.sourceTitle + '"' : '')
                + (discoveryAwaiting ? ' — needs your answer' : '…')
            ),
            discovery.phase === 'streaming' && stripStreamingXml(discovery.streamText)
              ? h('div', { className: 'copilot-md copilot-live-text' }, formatText(stripStreamingXml(discovery.streamText)))
              : null,
            discovery.phase === 'thinking'
              ? h('div', { className: 'copilot-live-placeholder' }, discovery.thinkingStatus || 'Working…')
              : null,
            discovery.probeEvents && discovery.probeEvents.length > 0
              ? h('div', { className: 'ai-flow-chat-probes' },
                  discovery.probeEvents.map(function(pe, i) {
                    var icon = pe.status === 'running' ? '⏳' : pe.status === 'ok' ? '✅' : '❌';
                    return h('div', { key: i, className: 'planner-probe-event planner-probe-' + pe.status },
                      h('span', { className: 'planner-probe-event-icon' }, icon),
                      h('span', { className: 'planner-probe-event-label' }, pe.label),
                      pe.output && pe.status !== 'running'
                        ? h('div', { className: 'planner-probe-event-output' }, pe.output)
                        : null
                    );
                  })
                )
              : null,
            discoveryAwaiting && discovery.question ? renderDiscoveryQuestion(discovery.question) : null
          )
        : null
    ),

    // Close-while-busy confirmation — closing stops the model loop, so it
    // never happens silently mid-turn (in-sheet bar, no native popups).
    confirmClose && (sending || discovery)
      ? h('div', { className: 'copilot-confirm-close' },
          h('span', { className: 'copilot-confirm-close-text' },
            'Copilot is still working — closing will stop the current task.'),
          h('button', {
            type: 'button',
            className: 'copilot-confirm-btn copilot-confirm-btn--stay',
            onClick: function() { setConfirmClose(false); }
          }, 'Keep working'),
          h('button', {
            type: 'button',
            className: 'copilot-confirm-btn copilot-confirm-btn--stop',
            onClick: stopAndClose
          }, 'Stop & close')
        )
      : null,

    // Input bar
    h('div', { className: 'copilot-inputbar' },
      h('textarea', {
        ref: inputRef,
        className: 'copilot-input',
        placeholder: discoveryAwaiting ? 'Answer the node builder…' : 'Describe the change…',
        rows: 1,
        value: input,
        // While a discovery waits for an answer the main input serves IT —
        // otherwise it stays locked during a running turn as before.
        disabled: sending && !discoveryAwaiting,
        onChange: function(e) { setInput(e.target.value); },
        onKeyDown: onInputKeyDown
      }),
      h('button', {
        type: 'button',
        className: 'copilot-send',
        title: 'Send',
        disabled: (sending && !discoveryAwaiting) || !input.trim(),
        onClick: function() { if (discoveryAwaiting) sendDiscoveryAnswer(input); else send(); }
      },
        h('svg', {
          width: 15, height: 15, viewBox: '0 0 24 24',
          fill: 'none', stroke: 'currentColor', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          h('path', { d: 'M12 19V5' }),
          h('path', { d: 'M5 12l7-7 7 7' })
        )
      )
    )
  );
}

module.exports = WorkflowCopilotPopup;
